import React, { useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import { useHistory } from 'react-router-dom';
import ReactInputMask from 'react-input-mask';

import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import EmojiEmotionsOutlinedIcon from '@material-ui/icons/EmojiEmotionsOutlined';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import ExitToAppOutlinedIcon from '@material-ui/icons/ExitToAppOutlined';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';

import * as S from './styles';
import { auth } from '../../services/firebase';
import { useGlobal } from '../../contexts/globalContext';

import UserRepository from '../../repositories/userRepository';
import Loader from '../Loader';

import { validEmail, validCPF } from '../../utils/validator';
import { getCampaignColors } from '../../utils/getCampaignColors';
import getTermByTag from '../../repositories/termRepository';
import SimpleModal from '../SimpleModal';

// Etapas do modal de identificação
const STEP_CHOICE = 'choice';
const STEP_REGISTER = 'register';
const STEP_LOGIN = 'login';

function RegisterModal({
	open,
	onClose,
	booking,
	paymentData,
	type,
	travelVoucherUser,
	mandatory,
}) {
	// Quando mandatory=true (CA001 Fluxo 1 — gate de identificação VV imediato),
	// o modal não pode ser fechado pelo X / clique fora / ESC. Único caminho é completar
	// cadastro ou login para vincular a conta.
	const isMandatory = Boolean(mandatory);
	const safeClose = (...args) => {
		if (isMandatory) return;
		if (typeof onClose === 'function') onClose(...args);
	};
	const [error, setError] = useState('');
	const [openLoader, setOpenLoader] = useState(false);
	const [formData, setFormData] = useState({});
	const [step, setStep] = useState(STEP_CHOICE);
	const [recoverPass, setRecoverPass] = useState(false);
	const [showPass, setShowPass] = useState(false);
	const [userName, setUserName] = useState('');
	const [pass, setPass] = useState('');
	const [openTerms, setOpenTerms] = useState(false);
	const [termsData, setTermsData] = useState(null);

	const { user, loginFirebase, showAlert } = useGlobal();
	const history = useHistory();

	const userRepository = new UserRepository();

	// Jornada: com Vale Viagem (transfere pontos) x Convidado (só troca de sessão)
	const isVoucher = Boolean(travelVoucherUser);
	const isRegister = step === STEP_REGISTER;

	// Cor de destaque da campanha (white-label); fallback no theme via styled.
	const accentColor = getCampaignColors()?.searchColor;

	const goToStep = (next) => {
		setError('');
		setRecoverPass(false);
		setStep(next);
	};

	const validIfFormIsFilled = () => {
		const { name, lastName, cpf, cellphone, email, password } = formData;

		if (name && lastName && cpf && cellphone && email && password) {
			setError('');
			return true;
		}
		return false;
	};

	const handleSubmit = async () => {
		setOpenLoader(true);
		if (isRegister) {
			await handleSubmitRegister();
		} else {
			await handleSubmitLogin();
		}
	};

	const handleSubmitRegister = async () => {
		const { name, lastName, cpf, cellphone, email, password } = formData;
		const data = {
			name,
			lastName,
			cpf,
			cellphone,
			email,
			password,
			campaign: user.campaign,
			customer: user.campaign.customer,
			referenceUserId: user.id,
			travelVoucher: travelVoucherUser,
		};

		if (!validIfFormIsFilled()) {
			setError('Campo obrigatório não preenchido, revise o cadastro.');
			setOpenLoader(false);
			return;
		}
		if (!validEmail(formData.email)) {
			setError('Por favor, digite um e-mail válido');
			setOpenLoader(false);
			return;
		}
		if (!validCPF(formData.cpf)) {
			setError('Por favor, digite um CPF válido');
			setOpenLoader(false);
			return;
		}

		const response = await userRepository.createNewUser(data);
		if (!response.data.error) {
			// A transferência VV já aconteceu no createNewUser. Limpar a sessão VV antiga
			// para o globalContext.login NÃO chamar loginFromVoucher novamente e disparar ALREADY_USED.
			sessionStorage.removeItem('@App:user');
			await loginFirebase(email, password).then(() => {
				showAlert('success', 'Cadastro realizado com sucesso!');
				// Em modo mandatory (gate VV pós-login na home) não tem reserva em andamento — fica na home.
				if (!isMandatory) history.push('/busca/resumo');
			});
		}

		if (response.data.error) {
			setOpenLoader(false);
			setError(response.data.error);
		}
	};

	const handleSubmitLogin = async () => {
		setError('');
		try {
			if (recoverPass) {
				if (userName !== '') {
					auth
						.sendPasswordResetEmail(userName)
						.then(() => {
							showAlert(
								'success',
								'Enviamos um e-mail para você atualizar sua senha, verifique!'
							);
						})
						.catch((e) => {
							setOpenLoader(false);
							setError('Por favor, digite um e-mail valido');
						});
				} else {
					setOpenLoader(false);
					setError('Por favor, digite um e-mail valido');
				}
			} else {
				if (userName !== '' && pass !== '') {
					await loginFirebase(userName, pass).then((result) => {
						if (!result?.success) {
							setError(result.error);
							setOpenLoader(false);
						} else {
							// Em modo mandatory (gate VV pós-login na home) não tem reserva em andamento — fica na home.
							if (!isMandatory) history.push('/busca/resumo');
						}
					});
				} else {
					setError('Por favor, digite um usuário e senha');
					setOpenLoader(false);
				}
			}
		} catch (e) {
			setError(e);
			setOpenLoader(false);
		}
	};

	const handleChangeInputs = (e) => {
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleEnter = (e) => {
		if (e.key === 'Enter') handleSubmit();
	};

	const handleOpenTerms = async () => {
		try {
			if (!termsData) {
				const res = await getTermByTag('USE_TERMS_PRIVACY_POLICY');
				setTermsData(res?.data);
			}
		} catch (e) {
			// abre mesmo sem conteúdo carregado
		}
		setOpenTerms(true);
	};

	const renderTopBar = () => (
		<S.FormTopBar>
			<S.BackButton type='button' onClick={() => goToStep(STEP_CHOICE)}>
				<ArrowBackIcon /> Voltar
			</S.BackButton>
			{!isMandatory && (
				<S.CloseButton type='button' aria-label='Fechar' onClick={() => safeClose(false)}>
					<CloseIcon />
				</S.CloseButton>
			)}
		</S.FormTopBar>
	);

	const passwordToggle = (
		<button
			type='button'
			className='toggle'
			aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
			onClick={() => setShowPass((s) => !s)}
		>
			{showPass ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
		</button>
	);

	const renderChoice = () => (
		<>
			<S.Banner isVoucher={isVoucher} accent={accentColor}>
				{isVoucher ? <ReportProblemOutlinedIcon /> : <EmojiEmotionsOutlinedIcon />}
				<span className='banner-text'>
					{isVoucher
						? 'O acesso com o Vale Viagem precisa ser vinculado a uma conta.'
						: 'Que bom te ver por aqui!'}
				</span>
				{!isMandatory && (
					<button
						type='button'
						className='banner-close'
						aria-label='Fechar'
						onClick={() => safeClose(false)}
					>
						<CloseIcon />
					</button>
				)}
			</S.Banner>

			<S.Body>
				<S.Heading>Falta pouco para sua viagem!</S.Heading>
				<S.Subheading>
					Para concluir sua reserva, você precisa usar uma conta Destino Férias.
				</S.Subheading>

				{isVoucher && (
					<S.PointsInfo accent={accentColor}>
						<span>
							<b>Não se preocupe</b>: seus pontos serão transferidos automaticamente
							para sua conta após o login ou cadastro.
						</span>
					</S.PointsInfo>
				)}

				<S.ChoiceGrid>
					<S.ChoiceCard variant='brand' accent={accentColor}>
						<span className='card-icon'>
							<PersonAddOutlinedIcon />
						</span>
						<p className='card-title'>Primeira compra? Crie sua conta</p>
						<p className='card-desc'>
							{isVoucher
								? 'Crie sua conta para salvar seus dados e transferir seus pontos.'
								: 'Crie sua conta para salvar seus dados e garantir sua reserva.'}
						</p>
						<S.PrimaryButton
							type='button'
							accent={accentColor}
							onClick={() => goToStep(STEP_REGISTER)}
						>
							Criar Minha Conta
						</S.PrimaryButton>
					</S.ChoiceCard>

					<S.ChoiceCard variant='neutral' accent={accentColor}>
						<span className='card-icon'>
							<ExitToAppOutlinedIcon />
						</span>
						<p className='card-title'>Já tenho cadastro</p>
						<p className='card-desc'>
							Use seu e-mail e senha cadastrados anteriormente.
						</p>
						<S.SecondaryButton
							type='button'
							accent={accentColor}
							onClick={() => goToStep(STEP_LOGIN)}
						>
							Entrar agora
						</S.SecondaryButton>
					</S.ChoiceCard>
				</S.ChoiceGrid>
			</S.Body>
		</>
	);

	const renderLoginForm = () => (
		<S.FormSection>
			{renderTopBar()}
			<S.FormHeading center>Acesse sua conta</S.FormHeading>
			<S.FormSubtitle center>
				{isVoucher
					? 'Entre para finalizar sua reserva e resgatar seus pontos.'
					: 'Entre para finalizar sua reserva.'}
			</S.FormSubtitle>

			<S.Field>
				<S.FieldLabel>E-mail</S.FieldLabel>
				<S.InputBox accent={accentColor}>
					<MailOutlinedIcon />
					<input
						type='email'
						placeholder='nome@exemplo.com'
						autoFocus
						value={userName}
						onChange={(e) => setUserName(e.target.value)}
						onKeyPress={handleEnter}
					/>
				</S.InputBox>
			</S.Field>

			{!recoverPass && (
				<S.Field>
					<S.FieldLabel>Senha</S.FieldLabel>
					<S.InputBox accent={accentColor}>
						<LockOutlinedIcon />
						<input
							type={showPass ? 'text' : 'password'}
							placeholder='••••••••'
							value={pass}
							onChange={(e) => setPass(e.target.value)}
							onKeyPress={handleEnter}
						/>
						{passwordToggle}
					</S.InputBox>
				</S.Field>
			)}

			<S.ForgotLink
				type='button'
				accent={accentColor}
				onClick={() => {
					setError('');
					setRecoverPass(!recoverPass);
				}}
			>
				{recoverPass ? 'Voltar ao login' : 'Esqueceu sua senha?'}
			</S.ForgotLink>

			{error && <S.ErrorText>{error}</S.ErrorText>}

			<S.PrimaryButton
				type='button'
				accent={accentColor}
				onClick={handleSubmit}
				style={{ marginTop: 18 }}
			>
				{recoverPass ? 'Recuperar senha' : 'Confirmar'}
			</S.PrimaryButton>

			<S.OrDivider>ou</S.OrDivider>

			<S.SecondaryButton
				type='button'
				accent={accentColor}
				onClick={() => goToStep(STEP_REGISTER)}
			>
				<PersonAddOutlinedIcon /> Novo por aqui? Criar conta
			</S.SecondaryButton>
		</S.FormSection>
	);

	const renderRegisterForm = () => (
		<S.FormSection>
			{renderTopBar()}
			<S.FormHeading brand accent={accentColor}>
				Crie sua conta
			</S.FormHeading>
			<S.FormSubtitle>
				{isVoucher
					? 'Falta pouco para sua viagem! Seus pontos do Vale Viagem serão transferidos automaticamente após o cadastro.'
					: 'Falta pouco para sua viagem! Crie sua conta para garantir sua reserva.'}
			</S.FormSubtitle>

			<S.FieldRow>
				<S.Field>
					<S.FieldLabel>Nome</S.FieldLabel>
					<S.InputBox accent={accentColor}>
						<input placeholder='Seu nome' name='name' onChange={handleChangeInputs} />
					</S.InputBox>
				</S.Field>
				<S.Field>
					<S.FieldLabel>Sobrenome</S.FieldLabel>
					<S.InputBox accent={accentColor}>
						<input placeholder='Seu sobrenome' name='lastName' onChange={handleChangeInputs} />
					</S.InputBox>
				</S.Field>
			</S.FieldRow>

			<S.Field>
				<S.FieldLabel>CPF</S.FieldLabel>
				<S.InputBox accent={accentColor}>
					<ReactInputMask mask='999.999.999-99' onChange={handleChangeInputs} name='cpf'>
						{(inputProps) => <input {...inputProps} name='cpf' placeholder='000.000.000-00' />}
					</ReactInputMask>
				</S.InputBox>
			</S.Field>

			<S.Field>
				<S.FieldLabel>Celular</S.FieldLabel>
				<S.InputBox accent={accentColor}>
					<ReactInputMask mask='(99) 99999-9999' onChange={handleChangeInputs} name='cellphone'>
						{(inputProps) => <input {...inputProps} name='cellphone' placeholder='(00) 00000-0000' />}
					</ReactInputMask>
				</S.InputBox>
			</S.Field>

			<S.Field>
				<S.FieldLabel>E-mail</S.FieldLabel>
				<S.InputBox accent={accentColor}>
					<input type='email' placeholder='seu@email.com' name='email' onChange={handleChangeInputs} />
				</S.InputBox>
			</S.Field>

			<S.Field>
				<S.FieldLabel>Senha</S.FieldLabel>
				<S.InputBox accent={accentColor}>
					<input
						type={showPass ? 'text' : 'password'}
						placeholder='••••••••'
						name='password'
						onChange={handleChangeInputs}
					/>
					{passwordToggle}
				</S.InputBox>
			</S.Field>

			{error && <S.ErrorText>{error}</S.ErrorText>}

			<S.PrimaryButton
				type='button'
				accent={accentColor}
				onClick={handleSubmit}
				style={{ marginTop: 8 }}
			>
				Confirmar
			</S.PrimaryButton>

			<S.Terms accent={accentColor}>
				Ao se cadastrar, você concorda com nossos{' '}
				<span className='link' onClick={handleOpenTerms}>
					Termos de Uso
				</span>{' '}
				e{' '}
				<span className='link' onClick={handleOpenTerms}>
					Política de Privacidade
				</span>
				.
			</S.Terms>

			<S.SwitchLink accent={accentColor}>
				Já tem conta?{' '}
				<span className='link' onClick={() => goToStep(STEP_LOGIN)}>
					Entrar
				</span>
			</S.SwitchLink>
		</S.FormSection>
	);

	const dialogMaxWidth = step === STEP_CHOICE ? 640 : isRegister ? 560 : 460;

	const renderStep = () => {
		if (step === STEP_REGISTER) return renderRegisterForm();
		if (step === STEP_LOGIN) return renderLoginForm();
		return renderChoice();
	};

	return (
		<S.Modal>
			<Loader
				openLoader={openLoader}
				isBooking={false}
				isRegister={isRegister}
				isLogin={!isRegister}
			/>
			<Dialog
				open={open}
				maxWidth={false}
				scroll='body'
				PaperProps={{ style: { width: '100%', maxWidth: dialogMaxWidth, borderRadius: 12 } }}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				disableBackdropClick={isMandatory}
				disableEscapeKeyDown={isMandatory}
				onClose={(_, reason) => {
					if (isMandatory && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
					safeClose(false);
				}}
			>
				{renderStep()}
			</Dialog>

			{openTerms && (
				<SimpleModal
					opened={openTerms}
					openedState={setOpenTerms}
					title={termsData?.description}
					text={termsData?.content}
					closeButtonText='Fechar'
					allowCloseByClickOutside={true}
					closeButtonDisabledTillScrollEnded={false}
				/>
			)}
		</S.Modal>
	);
}

export default RegisterModal;
